import { useEffect } from 'react';
import { useClerk } from '@clerk/clerk-react';
import { isAbortLikeError, logError } from '../lib/utils';

export const useClerkErrorHandler = () => {
  const clerk = useClerk();

  useEffect(() => {
    // Manejar errores de red específicos de Clerk
    const handleClerkNetworkError = (event) => {
      // Detectar errores específicos de Clerk
      if (event.target && event.target.src) {
        const src = event.target.src;
        if (src.includes('clerk') || src.includes('accounts.dev')) {
          console.warn('Error de red detectado en recurso de Clerk:', src);
          
          // Mostrar notificación al usuario
          if (window.showClerkError) {
            window.showClerkError('Error de conexión con el servicio de autenticación');
          }
        }
      }
    };

    // Manejar errores de JavaScript relacionados con Clerk
    const handleClerkJSError = (event) => {
      const error = event.error || event.reason;
      if (isAbortLikeError(error)) {
        event.preventDefault?.();
        return;
      }

      if (error && error.stack) {
        const errorString = error.toString().toLowerCase();
        const stackString = error.stack.toLowerCase();
        
        const clerkErrorPatterns = [
          'clerk',
          'accounts.dev',
          'clerk.js',
          'clerk-react',
          '__clerk_db_jwt',
          'redirecttosignin'
        ];
        
        const isClerkError = clerkErrorPatterns.some(pattern => 
          errorString.includes(pattern) || stackString.includes(pattern)
        );
        
        if (isClerkError) {
          logError('Error de Clerk detectado:', error);
          
          // Notificar al usuario sin recargar automáticamente
          if (window.showClerkError) {
            window.showClerkError('Hubo un problema con la autenticación. Por favor, intenta nuevamente.');
          }
        }
      }
    };

    // Manejar errores de fetch específicos
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      try {
        const response = await originalFetch(...args);
        
        // Verificar si la URL es de Clerk y hay error
        if (args[0] && typeof args[0] === 'string' && args[0].includes('clerk')) {
          if (!response.ok) {
            console.warn('Error en petición a Clerk:', response.status, response.statusText);
            
            if (response.status === 401 || response.status === 403) {
              // Error de autenticación
              if (window.showClerkError) {
                window.showClerkError('Sesión expirada. Por favor, inicia sesión nuevamente.');
              }
            }
          }
        }
        
        return response;
      } catch (error) {
        // Error de red en petición a Clerk
        if (args[0] && typeof args[0] === 'string' && args[0].includes('clerk')) {
          logError('Error de red en petición a Clerk:', error, { ignoreAbort: true });
          
          if (window.showClerkError) {
            window.showClerkError('Error de conexión con el servicio de autenticación');
          }
        }
        
        throw error;
      }
    };

    // Agregar listeners
    window.addEventListener('error', handleClerkNetworkError, true);
    window.addEventListener('error', handleClerkJSError);
    window.addEventListener('unhandledrejection', handleClerkJSError);

    // Función global para mostrar errores de Clerk
    window.showClerkError = (message) => {
      // Crear notificación visual
      const notification = document.createElement('div');
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: linear-gradient(135deg, #ff6b6b, #ee5a24);
        color: white;
        padding: 16px 20px;
        border-radius: 12px;
        box-shadow: 0 10px 25px rgba(255, 107, 107, 0.3);
        z-index: 10000;
        font-family: 'Inter', sans-serif;
        font-size: 14px;
        font-weight: 500;
        max-width: 350px;
        animation: slideInRight 0.3s ease-out;
      `;
      
      notification.innerHTML = `
        <div style="display: flex; align-items: center; gap: 8px;">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="white"/>
            <path d="M12 7v6m0 4h.01" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>${message}</span>
        </div>
      `;
      
      // Agregar estilos de animación
      const style = document.createElement('style');
      style.textContent = `
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `;
      document.head.appendChild(style);
      
      document.body.appendChild(notification);
      
      // Remover después de 5 segundos
      setTimeout(() => {
        if (notification.parentNode) {
          notification.style.animation = 'slideInRight 0.3s ease-out reverse';
          setTimeout(() => {
            notification.remove();
            style.remove();
          }, 300);
        }
      }, 5000);
    };

    // Cleanup
    return () => {
      window.removeEventListener('error', handleClerkNetworkError, true);
      window.removeEventListener('error', handleClerkJSError);
      window.removeEventListener('unhandledrejection', handleClerkJSError);
      window.fetch = originalFetch;
      delete window.showClerkError;
    };
  }, [clerk]);

  return {
    // Función para reportar errores manualmente
    reportClerkError: (error) => {
      logError('Error de Clerk reportado manualmente:', error, { ignoreAbort: true });
      if (window.showClerkError) {
        window.showClerkError('Se ha detectado un problema de autenticación');
      }
    },
    
    // Función para reintentar autenticación
    retryAuth: () => {
      if (clerk) {
        window.location.href = window.location.href;
      }
    }
  };
};
